import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { MobileShell } from "@/components/MobileShell";
import { TownMap } from "@/components/TownMap";
import { Avatar } from "@/components/AppHeader";
import api, { formatApiError } from "@/lib/api";
import {
  ThumbsUp, MessageCircle, Loader2, MapPin, ChevronLeft, Share2, AlertOctagon, Send,
  Edit3, Trash2, X,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { t } from "@/lib/strings";
import { timeAgo } from "@/components/Cards";

const STATUS_OPTIONS = [
  { value: "open", label: { ar: "مفتوح", he: "פתוח", en: "Open" } },
  { value: "in_progress", label: { ar: "قيد المعالجة", he: "בטיפול", en: "In Progress" } },
  { value: "resolved", label: { ar: "تم الحل", he: "נפתר", en: "Resolved" } },
];

export default function ReportDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { language } = useI18n();
  const [report, setReport] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({ title: "", description: "" });
  const [savingEdit, setSavingEdit] = useState(false);

  const isAr = language === "ar"; const isHe = language === "he";
  const L = (ar, he, en) => isAr ? ar : isHe ? he : en;

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [r, c] = await Promise.all([
          api.get(`/reports/${id}`),
          api.get(`/comments/report/${id}`),
        ]);
        setReport(r.data);
        setComments(c.data);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, [id]);

  const toggleLike = async () => {
    try {
      const { data } = await api.post(`/reports/${id}/like`);
      setReport((r) => ({ ...r, liked_by_me: data.liked, likes_count: data.likes_count }));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/comments/report/${id}`, { content: newComment.trim() });
      setComments((cs) => [...cs, data]);
      setNewComment("");
      setReport((r) => ({ ...r, comments_count: (r.comments_count || 0) + 1 }));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const updateStatus = async (value) => {
    try {
      await api.patch(`/reports/${id}/status`, { status: value });
      setReport((r) => ({ ...r, status: value }));
      toast.success(L("تم تحديث الحالة", "הסטטוס עודכן", "Status updated"));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const shareReport = async () => {
    try {
      const url = window.location.href;
      if (navigator.share) await navigator.share({ title: report?.title, url });
      else { await navigator.clipboard.writeText(url); toast.success(L("تم نسخ الرابط", "הקישור הועתק", "Link copied")); }
    } catch { /* user cancelled */ }
  };

  const canEdit =
    !!user && !!report &&
    (String(user.id) === String(report.author?.id) || user.role === "admin");

  const startEdit = () => {
    if (!report) return;
    setEditForm({ title: report.title || "", description: report.description || "" });
    setEditing(true);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editForm.title.trim() || !editForm.description.trim()) return;
    setSavingEdit(true);
    try {
      const { data } = await api.patch(`/reports/${id}`, {
        title: editForm.title.trim(),
        description: editForm.description.trim(),
      });
      setReport((r) => ({ ...r, ...data }));
      setEditing(false);
      toast.success(L("تم الحفظ", "נשמר", "Saved"));
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSavingEdit(false); }
  };

  const deleteReport = async () => {
    if (!window.confirm(L("هل أنت متأكد من حذف هذا البلاغ؟", "האם למחוק את הדיווח?", "Delete this report?"))) return;
    try {
      await api.delete(`/reports/${id}`);
      toast.success(L("تم الحذف", "נמחק", "Deleted"));
      navigate("/reports");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const topBar = (
    <div className="h-14 px-4 flex items-center gap-2">
      <button
        onClick={() => navigate(-1)}
        data-testid="detail-back-btn"
        className="w-9 h-9 rounded-full bg-[var(--te-input-bg)] hover:bg-[#E5E7F3] flex items-center justify-center"
      >
        <ChevronLeft size={20} />
      </button>
      <h1 className="font-extrabold text-[18px] text-[var(--te-text)] flex-1 truncate" dir="auto">
        {report?.title || L("بلاغ", "דיווח", "Report")}
      </h1>
      {canEdit && !editing && (
        <button
          onClick={startEdit}
          data-testid="detail-edit-btn"
          className="w-9 h-9 rounded-full bg-[var(--te-input-bg)] hover:bg-[#E5E7F3] flex items-center justify-center"
          title={L("تعديل", "ערוך", "Edit")}
        >
          <Edit3 size={15} />
        </button>
      )}
      {canEdit && !editing && (
        <button
          onClick={deleteReport}
          data-testid="detail-delete-btn"
          className="w-9 h-9 rounded-full bg-[var(--te-danger-soft)] hover:bg-[#FED7D7] flex items-center justify-center text-[var(--te-danger)]"
          title={L("حذف", "מחק", "Delete")}
        >
          <Trash2 size={15} />
        </button>
      )}
      <button
        onClick={shareReport}
        data-testid="detail-share-btn"
        className="w-9 h-9 rounded-full bg-[var(--te-input-bg)] hover:bg-[#E5E7F3] flex items-center justify-center"
      >
        <Share2 size={16} />
      </button>
    </div>
  );

  if (loading) {
    return (
      <MobileShell topBar={topBar}>
        <div className="py-24 flex justify-center"><Loader2 className="animate-spin text-[var(--te-primary)]" /></div>
      </MobileShell>
    );
  }
  if (!report) {
    return (
      <MobileShell topBar={topBar}>
        <div className="text-center py-24 text-[var(--te-text-secondary)]">{L("لم يُعثر على البلاغ.", "הדיווח לא נמצא.", "Report not found.")}</div>
      </MobileShell>
    );
  }

  const isEmergency = (report.category || "").toLowerCase() === "emergency";
  const statusBadge = { open: "te-badge te-badge-open", in_progress: "te-badge te-badge-progress", resolved: "te-badge te-badge-resolved" }[report.status] || "te-badge te-badge-open";
  const statusLabel = STATUS_OPTIONS.find((s) => s.value === report.status)?.label[language] || report.status;

  return (
    <MobileShell topBar={topBar}>
      <div className="px-4 pt-4 pb-6">
        <article className={`te-card te-fade-up overflow-hidden ${isEmergency ? "border-2 !border-[var(--te-danger)]" : ""}`}>
          {isEmergency && (
            <div className="px-4 py-1.5 bg-[var(--te-danger)] text-white text-[11px] font-bold inline-flex items-center gap-1">
              <AlertOctagon size={12} /> {t("emergency", language)}
            </div>
          )}
          <div className="p-5">
            <div className="flex items-center gap-3 mb-4">
              <Avatar user={report.author} size={42} />
              <div className="flex-1 min-w-0">
                <Link to={`/profile/${report.author?.id}`} className="font-bold text-[15px] text-[var(--te-text)] hover:underline" dir="auto">
                  {report.author?.name}
                </Link>
                <div className="text-[12px] text-[var(--te-text-muted)]" dir="auto">
                  {timeAgo(report.created_at)} · <span className="capitalize">{(report.category || "").replace("_", " ")}</span>
                </div>
              </div>
              <span className={statusBadge}>{statusLabel}</span>
            </div>

            <h2 className="font-extrabold text-[22px] text-[var(--te-text)] leading-tight" dir="auto">
              {report.title}
            </h2>
            {editing && (
              <form onSubmit={saveEdit} className="mt-4 space-y-3 te-fade-up" data-testid="detail-edit-form">
                <input
                  className="te-input"
                  value={editForm.title}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  data-testid="detail-edit-title"
                  dir="auto"
                />
                <textarea
                  className="te-input min-h-[110px] resize-y"
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  data-testid="detail-edit-description"
                  dir="auto"
                />
                <div className="flex gap-2 justify-end">
                  <button type="button" onClick={() => setEditing(false)} className="te-btn-secondary text-[13px]">
                    <X size={13} /> {L("إلغاء", "ביטול", "Cancel")}
                  </button>
                  <button type="submit" disabled={savingEdit} data-testid="detail-edit-save" className="te-btn-primary text-[13px]">
                    {savingEdit ? <Loader2 size={13} className="animate-spin" /> : null}
                    {L("حفظ", "שמור", "Save")}
                  </button>
                </div>
              </form>
            )}
            {report.address && (
              <div className="flex items-center gap-1.5 text-sm text-[var(--te-text-secondary)] mt-2" dir="auto">
                <MapPin size={14} /> {report.address}
              </div>
            )}
            <p className="mt-4 text-[var(--te-text)] leading-relaxed whitespace-pre-wrap text-[15px]" dir="auto">
              {report.description}
            </p>

            {report.image_url && (
              <img
                src={report.image_url}
                alt={report.title}
                className="mt-4 rounded-2xl w-full max-h-[440px] object-cover border border-[var(--te-divider)]"
              />
            )}

            {report.lat && report.lng && (
              <div className="mt-4">
                <TownMap reports={[report]} center={[report.lat, report.lng]} zoom={15} height={240} />
              </div>
            )}

            <div className="mt-5 pt-4 border-t border-[var(--te-divider)] flex items-center gap-2">
              <button
                onClick={toggleLike}
                data-testid="detail-like-btn"
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                  report.liked_by_me ? "bg-[var(--te-primary-soft)] text-[var(--te-primary)]" : "text-[var(--te-text-secondary)] hover:bg-[var(--te-bg-soft)]"
                }`}
              >
                <ThumbsUp size={15} fill={report.liked_by_me ? "currentColor" : "none"} /> {report.likes_count}
              </button>
              <div className="flex items-center gap-1.5 px-3 py-2 text-sm text-[var(--te-text-secondary)]">
                <MessageCircle size={15} /> {comments.length}
              </div>
              {user?.role === "admin" && (
                <select
                  value={report.status}
                  onChange={(e) => updateStatus(e.target.value)}
                  className="te-input max-w-[160px] !py-2 text-sm ml-auto"
                  data-testid="admin-status-select"
                >
                  {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label[language] || s.label.en}</option>)}
                </select>
              )}
            </div>
          </div>
        </article>

        <section className="mt-6">
          <h2 className="font-extrabold text-[16px] text-[var(--te-text)] mb-3 px-1">
            {L("التعليقات", "תגובות", "Comments")} ({comments.length})
          </h2>
          <form onSubmit={submitComment} className="te-card p-4 mb-4 flex gap-3 items-start">
            <Avatar user={user} size={36} />
            <div className="flex-1">
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder={L("أضف تعليقك…", "הוסף תגובה…", "Add a comment…")}
                className="te-input min-h-[60px] resize-y text-sm"
                data-testid="comment-input"
                dir="auto"
              />
              <div className="flex justify-end mt-2">
                <button
                  type="submit"
                  disabled={busy || !newComment.trim()}
                  className="te-btn-primary text-[13px] !py-2 !px-4"
                  data-testid="comment-submit-btn"
                >
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={13} />}
                  {L("نشر", "פרסם", "Post")}
                </button>
              </div>
            </div>
          </form>

          {comments.length === 0 ? (
            <div className="text-center text-sm text-[var(--te-text-muted)] py-8">
              {L("كن أول من يعلّق.", "היה הראשון להגיב.", "Be the first to comment.")}
            </div>
          ) : (
            <div className="space-y-2.5">
              {comments.map((c) => (
                <div key={c.id} className="te-card p-3.5 flex gap-3" data-testid={`comment-${c.id}`}>
                  <Avatar user={c.author} size={34} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2">
                      <Link to={`/profile/${c.author?.id}`} className="font-bold text-[13px] text-[var(--te-text)] hover:underline" dir="auto">
                        {c.author?.name}
                      </Link>
                      <span className="text-[11px] text-[var(--te-text-muted)]">{timeAgo(c.created_at)}</span>
                    </div>
                    <p className="text-[13px] text-[var(--te-text)] mt-1 whitespace-pre-wrap leading-relaxed" dir="auto">
                      {c.content}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </MobileShell>
  );
}
